import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateReview } from '@/features/reviews/hooks';
import { useMatters } from '@/features/matters/hooks';

interface K3RatingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lawyerId: number;
  lawyerName: string;
  onSuccess?: () => void;
}

const RATING_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Below Expectations',
  3: 'Meets Expectations',
  4: 'Exceeds Expectations',
  5: 'Exceptional',
};

function getRatingColor(score: number, selected: number | null): string {
  const base = 'h-10 w-10 rounded-full border-2 font-semibold text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ';
  const isSelected = selected === score;
  if (score === 5) {
    return base + (isSelected ? 'bg-green-500 border-green-500 text-white scale-110 shadow' : 'border-green-300 text-green-700 hover:bg-green-100');
  }
  if (score === 4) {
    return base + (isSelected ? 'bg-emerald-400 border-emerald-400 text-white scale-110 shadow' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-100');
  }
  if (score === 3) {
    return base + (isSelected ? 'bg-amber-400 border-amber-400 text-white scale-110 shadow' : 'border-amber-300 text-amber-700 hover:bg-amber-100');
  }
  if (score === 2) {
    return base + (isSelected ? 'bg-orange-500 border-orange-500 text-white scale-110 shadow' : 'border-orange-300 text-orange-700 hover:bg-orange-100');
  }
  return base + (isSelected ? 'bg-red-500 border-red-500 text-white scale-110 shadow' : 'border-red-300 text-red-700 hover:bg-red-100');
}

export function K3RatingDialog({
  open,
  onOpenChange,
  lawyerId,
  lawyerName,
  onSuccess,
}: K3RatingDialogProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<number | null>(null);
  const [comment, setComment] = React.useState('');
  const [matterId, setMatterId] = React.useState<string>('');
  const { mutate: createReview, isPending } = useCreateReview();

  const { data: mattersData, isLoading: mattersLoading } = useMatters({
    userId: lawyerId,
    limit: 100,
  });

  React.useEffect(() => {
    if (open) {
      setSelected(null);
      setComment('');
      setMatterId('');
    }
  }, [open]);

  const handleSubmit = () => {
    if (selected === null || !matterId) return;
    createReview(
      { rating: selected, comment, userId: lawyerId, matterId: Number(matterId) },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  const selectedLabel = selected !== null ? RATING_LABELS[selected] : null;
  const matters = mattersData?.results ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('k3.dialogTitle')}</DialogTitle>
          <DialogDescription className="text-slate-700 font-medium mt-0.5">
            {lawyerName}
          </DialogDescription>
        </DialogHeader>

        {/* Matter select */}
        <div className="space-y-1.5">
          <label className="text-sm text-slate-600">{t('matter')}</label>
          <Select value={matterId} onValueChange={setMatterId} disabled={mattersLoading}>
            <SelectTrigger>
              <SelectValue placeholder={mattersLoading ? t('loading') : t('k3.selectMatter')} />
            </SelectTrigger>
            <SelectContent>
              {matters.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {m.name || m.caseNumber || `#${m.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Rating buttons 1–5 */}
        <div className="space-y-2">
          <p className="text-sm text-slate-600">{t('k3.selectRating')}</p>
          <div className="flex items-center justify-center gap-3">
            {[1, 2, 3, 4, 5].map((score) => (
              <button
                key={score}
                type="button"
                onClick={() => setSelected(score)}
                className={getRatingColor(score, selected)}
                aria-pressed={selected === score}
              >
                {score}
              </button>
            ))}
          </div>
          {selectedLabel && (
            <p className="text-sm font-medium text-slate-600">
              {selected} — <span className="text-slate-800">{selectedLabel}</span>
            </p>
          )}
        </div>

        {/* Comment */}
        <div className="space-y-1.5">
          <label className="text-sm text-slate-600">
            {t('comment')} <span className="text-slate-400">({t('optional')})</span>
          </label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('k3.commentPlaceholder')}
            rows={3}
            className="resize-none"
          />
        </div>

        {/* K3 Formula */}
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-0.5">
          <p className="font-semibold text-slate-700 mb-1">{t('k3.formulaTitle')}</p>
          <p><span className="font-mono">K3_norm = (K3 − 1) / 4</span></p>
          <p className="text-slate-400 italic">{t('k3.formulaNote')}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={selected === null || !matterId || isPending}>
            {isPending ? t('processing') : t('k3.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
